import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Circle, ListChecks, Loader2, Minus, ShieldAlert, Table2 } from "lucide-react";

// The agent run's live presence in the chat: ONE message slot that morphs.
//   1. AgentRunStatus — a fixed-height status line ("Pulling share by channel… · 24s")
//      with a "Show working" expander: the live activity chips and verification
//      rendered as a phase (never as an alarm — a revision round is a spinner and the
//      words "double-checking", never red). It does NOT list the plan: on a pinned
//      flow that checklist was the flow's fixed sections, the same ten lines on every
//      question, telling a reader nothing about THIS run. The sequence the run
//      actually walked belongs to the working panel, one button away.
//   2. AgentMethodChip — what the timeline collapses into on the final answer:
//      "Share-loss playbook · Verified", expandable to the finished checklist.
// Fed exclusively by useAgentProgress state and the answer's `agent_meta` field;
// nothing here knows agent internals.

const MAX_VISIBLE_TOOLS = 3;

// What the chip says about the checking. THE BACKEND NAMES IT — `agent_meta.label`, with
// its own wording in `label_text` (since 2026-10-17: on the 2026-10-16 golden baseline 32
// of 59 answers that read "Verified" were wrong). Answers stored before that carry only
// `verified`/`status` and are read as they were, except that "Checked with caveats" now
// says what it meant: "Not verified". The green tick belongs to `verified` alone.
const LABEL_TEXT = {
  verified: "Verified",
  fit_checked: "Figures and fit checked",
  figures_traced: "Figures checked, fit not confirmed",
  unverified: "Not verified",
  partial: "Partial",
  declined: "Could not answer reliably",
  asked: "Asked a question back",
  failed: "Failed",
};

export function verdictOfMeta(meta) {
  const label = meta.label
    || (meta.verified ? "verified" : meta.status === "limit_tripped" ? "partial" : "unverified");
  const text = meta.label_text || LABEL_TEXT[label] || LABEL_TEXT.unverified;
  return { label, text, verified: label === "verified" };
}

// The icon the checks end on: green only when the run's final state says they passed;
// amber when they finished with something unconfirmed (or the stream ended without
// saying); red when the run failed. The chat has no amber token, so the hex is here.
const AMBER = "#d97706";

function FinalIcon({ state, size }) {
  if (state === "done") return <Check size={size} style={{ color: "var(--success-dot)" }} />;
  if (state === "failed") return <AlertTriangle size={size} style={{ color: "var(--error-border)" }} />;
  return <ShieldAlert size={size} style={{ color: AMBER }} />;
}

function finalWord(state) {
  if (state === "done") return "Checked";
  if (state === "failed") return "Failed";
  return "Checks finished";
}

function useElapsed(startedAt, running) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!startedAt) return "";
  const seconds = Math.max(0, Math.floor(now - startedAt));
  return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
}

function StepIcon({ state }) {
  if (state === "done") return <Check size={13} style={{ color: "var(--success-dot)" }} />;
  if (state === "running") return <Loader2 size={13} className="animate-spin" style={{ color: "var(--brand-primary)" }} />;
  if (state === "dropped") return <Minus size={13} style={{ color: "var(--text-tertiary)" }} />;
  return <Circle size={9} style={{ color: "var(--text-tertiary)" }} />;
}

function StepList({ steps }) {
  return (
    <div className="flex flex-col gap-1">
      {steps.map((step, index) => (
        <div key={index} className="flex items-start gap-2">
          <span className="mt-0.5 flex-shrink-0 w-4 flex justify-center">
            <StepIcon state={step.state} />
          </span>
          <span
            className="text-xs leading-5"
            style={{
              color: step.state === "todo" || step.state === "dropped"
                ? "var(--text-tertiary)" : "var(--text-primary)",
              textDecoration: step.state === "dropped" ? "line-through" : "none",
            }}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChecksRow({ progress }) {
  const inCheckPhase = progress.phase === "check" || progress.phase === "done";
  if (!inCheckPhase || !progress.checksDetail) return null;
  const done = progress.state === "done" || progress.finished;
  const revising = progress.state === "revising";
  return (
    <div
      className="mt-2 px-2.5 py-1.5 rounded-md text-[11px] leading-5"
      style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }}
    >
      <span className="flex items-center gap-1.5 flex-wrap">
        {done ? (
          <FinalIcon state={progress.finalState} size={12} />
        ) : (
          <Loader2 size={12} className="animate-spin" style={{ color: "var(--brand-primary)" }} />
        )}
        {revising ? progress.label : done ? finalWord(progress.finalState) : "Checking"}
        {!revising && <span>— {progress.checksDetail}</span>}
        {progress.round > 1 && !revising && <span>(pass {progress.round})</span>}
      </span>
    </div>
  );
}

export function AgentRunStatus({ progress, panelOpen = false, onOpenWork = null }) {
  const [expanded, setExpanded] = useState(false);
  const elapsed = useElapsed(progress.startedAt, !progress.finished);
  const hasWorking = progress.tools.length > 0 || !!progress.checksDetail;

  const headline = progress.stalled
    ? "Still working — the live view lost its connection"
    : progress.label || "Working on it";

  return (
    <div className="flex justify-start">
      <div
        className="max-w-[90%] max-sm:max-w-[92%] min-w-[260px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl rounded-bl-md text-xs sm:text-sm"
        style={{
          background: "var(--card-bg)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-color)",
        }}
      >
        <div className="flex items-center gap-2">
          <Loader2 size={14} className="animate-spin flex-shrink-0" style={{ color: "var(--brand-primary)" }} />
          <span className="min-w-0 truncate" style={{ color: "var(--text-primary)" }}>
            {headline}
          </span>
          {elapsed && (
            <span className="ml-auto pl-2 flex-shrink-0 text-[11px] tabular-nums" style={{ color: "var(--text-tertiary)" }}>
              {elapsed}
            </span>
          )}
        </div>
        {progress.detail && !expanded && progress.phase !== "check" && (
          <div className="mt-0.5 pl-6 text-[11px] truncate" style={{ color: "var(--text-tertiary)" }}>
            {progress.detail}
          </div>
        )}

        {/* THE WAY BACK IN. Closing the panel mid-run used to be final: nothing offered to
            reopen it until the answer landed (reported 2026-09-08). This button is here
            for the whole run, and says which of the two states it will put you in. */}
        {onOpenWork && (
          <button
            type="button"
            className="mt-1.5 flex items-center gap-1 text-[11px] font-medium transition-colors hover:opacity-80"
            style={{ color: "var(--brand-primary)" }}
            onClick={onOpenWork}
            data-open-working="true"
          >
            {panelOpen ? "Shown in the working panel" : "View Details"}
            <ChevronRight size={12} />
          </button>
        )}

        {hasWorking && (
          <button
            className="mt-1.5 flex items-center gap-1 text-[11px] font-medium transition-colors hover:opacity-80"
            style={{ color: "var(--text-secondary)" }}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {expanded ? "Hide working" : "Show working"}
          </button>
        )}

        {expanded && (
          <div className="mt-2 pt-2" style={{ borderTop: "1px dashed var(--border-color)" }}>
            {progress.tools.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {progress.tools.slice(-MAX_VISIBLE_TOOLS).map((tool, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]"
                    style={{
                      border: "1px solid var(--border-color)",
                      color: "var(--text-secondary)",
                      background: "var(--bg-secondary)",
                    }}
                  >
                    {tool.state === "running" && <Loader2 size={9} className="animate-spin" />}
                    {tool.label}
                  </span>
                ))}
              </div>
            )}
            <ChecksRow progress={progress} />
          </div>
        )}
      </div>
    </div>
  );
}

export function AgentMethodChip({ meta, run, onOpenWork }) {
  const [expanded, setExpanded] = useState(false);
  if (!meta) return null;

  const verdict = verdictOfMeta(meta);
  // THE RUN'S OWN NAME, not the playbook's. Every answer from one playbook carried the
  // same chip ("Combined status-query flow (one-shot narrative)"), which told a reader
  // scrolling a transcript nothing about which question it answered. `title` is the
  // model's own, from its first plan — the same string that heads the working panel.
  // Older answers stored before `title` existed keep showing the method.
  const method = meta.title || meta.method || "Analysis";
  const stepsCount = run?.steps?.length || 0;
  // HOW THE QUESTION WAS READ — the backend's one line from the catalogue's display names
  // (`agent_meta.read_as`, sent when the client's `reading_shown` flag is on). It opens
  // with the checklist, so an answer reloaded without its live run can still be checked
  // against what the system understood.
  const readAs = typeof meta.read_as === "string" ? meta.read_as.trim() : "";
  const expandable = (!!run && (run.steps.length > 0 || run.checksDetail)) || !!readAs;

  return (
    <div className="mb-2">
      <button
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${expandable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
        style={{
          border: "1px solid var(--border-color)",
          color: "var(--text-secondary)",
          background: "var(--bg-secondary)",
        }}
        onClick={expandable ? () => setExpanded((value) => !value) : undefined}
        title={expandable ? "Show how this was checked" : undefined}
      >
        <ListChecks size={12} style={{ color: "var(--brand-primary)" }} />
        <span>{method}</span>
        {stepsCount > 0 && <span>· {stepsCount} steps</span>}
        <span className="inline-flex items-center gap-0.5">
          · {verdict.text}
          {verdict.verified && <Check size={11} style={{ color: "var(--success-dot)" }} />}
        </span>
        {expandable && (expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />)}
      </button>
      {meta.work_url && onOpenWork && (
        <button
          className="ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer hover:opacity-80 transition-opacity"
          style={{
            border: "1px solid var(--brand-primary)",
            color: "var(--brand-primary)",
            background: "transparent",
          }}
          onClick={() => onOpenWork(meta.work_url, method)}
          title="Every table this answer was read from"
        >
          <Table2 size={12} />
          View Details
        </button>
      )}
      {expanded && (run || readAs) && (
        <div
          className="mt-1.5 px-3 py-2 rounded-lg"
          style={{ border: "1px solid var(--border-color)", background: "var(--card-bg)" }}
        >
          {readAs && (
            <div className="mb-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }} data-chip-readas="true">
              <span className="font-medium">Read as:</span> {readAs}
            </div>
          )}
          {run && run.methodLabel && (
            <div className="mb-1.5 text-[11px] font-medium" style={{ color: "var(--text-secondary)" }}>
              {run.methodLabel}
            </div>
          )}
          {run && run.steps.length > 0 && <StepList steps={run.steps} />}
          {run && run.checksDetail && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
              <FinalIcon state={run.finalState} size={12} />
              {run.checksDetail}
              {run.round > 1 && <span>(after {run.round} passes)</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
